import React, { useContext, useEffect } from "react";
import {
  Stepper,
  StepLabel,
  AcquirerFormRootArea,
  GridItem,
  GridContainer,
} from "./AcquirerForm.styles";
import { Button, Step } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setActiveStep } from "./AcquirerForm.slice";
import AcquirerInfo from "./AcquirerForm.acquirerInfo";
import AcquirerCommissions from "./AcquirerForm.acquirerCommissions";
import BankInfo from "./AcquirerForm.bankInfo";
import { useFormik } from "formik";
import {
  acquirerInitialValues,
  acquirerCreateValidationSchema,
  acquirerUpdateValidationSchema,
  setAcquirerValues,
} from "./AcquirerForm.resources";
import { createAcquirer } from "./AcquirerForm.thunk";
import AuthContext from "../../../context/AuthContext";
import { useEffectOnce } from "../../../utils/effects";
import {
  getAcquirerById,
  updateAcquirer,
  getCurrencies,
} from "./AcquirerForm.thunk";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import {
  createEnrollmentLoad,
  updateEnrollmentLoad,
  getEnrollmentByIdLoad,
} from "../../lists/LoadEnrollment/LoadEnrollment.thunk";
import {
  SetEnrollmentSelected,
  ResetDataFilteredByid,
} from "../../lists/LoadEnrollment/LoadEnrollment.slice";

const AcquirerForm = (props) => {
  const {
    setSubmenuAdministrativeOpen,
    setSubmenuAdministrativeOption,
    setSelectedOption,
    setNameSection,
    goBack,
    setSnackOpen,
    setMessageOpen,
    setSeverity,
  } = props;
  const dispatch = useDispatch();
  const {
    token,
    userInfo,
    acquirerData: {
      acquirerId: storeAcquirerId,
      acquirerName: storeAcquirerName,
    },
  } = useContext(AuthContext);
  const {
    activeStep,
    steps,
    status,
    acquirerData,
    acquirerId,
    statusRetrieve,
    currencies,
  } = useSelector((state) => state.acquirerForm);
  const modeForm = acquirerId ? "update" : "create";
  const validationSchema =
    modeForm === "create"
      ? acquirerCreateValidationSchema
      : acquirerUpdateValidationSchema;
  const { acquirerSelected } = useSelector((state) => state.acquirer);
  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;

  // Agregar selector del enrollment
  const {
    enrollmentDataById,
    statusRetrieve: statusRetrieveEnrollment,
    enrollmentSelected,
  } = useSelector((state) => state.loadEnrollment);

  useEffectOnce(() => {
    if (acquirerSelected) {
      dispatch(getAcquirerById({ token, acquirerId: acquirerSelected }));
    }
    if (enrollmentSelected) {
      dispatch(getEnrollmentByIdLoad({ token, id: enrollmentSelected }));
    }
    dispatch(getCurrencies({ token }));
  });

  useEffect(() => {
    if (
      enrollmentSelected &&
      enrollmentDataById &&
      statusRetrieveEnrollment === "succeeded"
    ) {
      const { data, current_step } = enrollmentDataById;
      setAcquirerValues({ acquirerData: data, setFieldValue });
      dispatch(
        setActiveStep(current_step > 0 ? current_step - 1 : current_step),
      );
      dispatch(SetEnrollmentSelected({ enrollmentSelected: null }));
    }
  }, [
    enrollmentDataById,
    enrollmentSelected,
    statusRetrieveEnrollment,
    setAcquirerValues,
  ]);

  useEffect(() => {
    return () => {
      dispatch(ResetDataFilteredByid());
    };
  }, []);

  const handleGoBack = () => {
    setSubmenuAdministrativeOpen(false);
    setSubmenuAdministrativeOption("acquirer_list");
    setSelectedOption("acquirer_list");
    setNameSection("Administrative - Acquirer");
    if (!acquirerSelected) {
      dispatch(
        updateEnrollmentLoad({
          token,
          formData: values,
          userMail: userInfo?.email || "",
          enrollmentId: enrollmentDataById,
          currentStep: activeStep,
          enrollment_completed: true,
          enrollment_type: "ACQUIRER",
        }),
      );
    }
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
    isSubmitting,
  } = useFormik({
    initialValues: acquirerInitialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        // Save enrollment load when form is completed
        if (modeForm === "create") {
          await dispatch(
            createAcquirer({
              acquirerData: {
                acquirerId: storeAcquirerId,
                acquirerName: storeAcquirerName,
                currency_code: values.fiat.currencyCode,
              },
              token,
              handleGoBack,
              email: values.email,
              withISO: Boolean(values.withISO),
              ...values,
            }),
          ).unwrap();
        } else {
          await dispatch(
            updateAcquirer({ token, handleGoBack, acquirerId, ...values }),
          ).unwrap();
        }
        goBack();
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Effect to set form values when acquirerData changes (for editing existing acquirer)
  useEffect(() => {
    if (acquirerData) {
      setAcquirerValues({ acquirerData, setFieldValue });
    }
  }, [acquirerData, setFieldValue]);

  // Force validation when values change
  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateForm();
    }
  }, [values, validateForm]);

  const handleStepClick = (index) => {
    if (index < activeStep) {
      dispatch(setActiveStep(index));
      return;
    }
    dispatch(setActiveStep(index));
  };

  const handleBack = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  /**
   * Validates the current step before allowing to proceed to the next step
   * @returns {Promise<boolean>} - True if validation passes, false otherwise
   */
  const validateCurrentStep = async () => {
    try {
      // Get all validation errors
      const validationErrors = await validateForm();

      // Define which fields belong to each step
      const stepFields = {
        "Acquirer Info": [
          "acquirerName",
          "acquirer.city",
          "acquirer.state",
          "acquirer.address",
          "acquirer.postalCode",
          "acquirer.countryCode",
          "fiat.currencyCode",
        ],
        "Acquirer Commissions": [
          "blokko.effectiveDate",
          "blokko.amountTxProcessing",
          "blokko.amountTerminalSetup",
          "blokko.percentTxProcessing",
          "blokko.amountTxProcessingTiers",
          "blokko.amountTerminalSetupTiers",
          "blokko.amountTerminalMaintenance",
          "blokko.terminalMaintenanceInactive",
        ],
        "Bank Info": [
          "bank.Iban",
          "bank.swiftCode",
          "bank.routingNumber",
          "bank.accountNumber",
          "bank.branchNumber",
          "bank.Bid",
          "bank.address",
          "bank.city",
          "bank.state",
          "bank.postalCode",
          "bank.countryCode",
        ],
      };

      // Get current step name
      const currentStepName = steps[activeStep];
      const currentStepFields = stepFields[currentStepName] || [];

      // Check if there are any errors in the current step fields
      const stepErrors = currentStepFields.filter((fieldPath) => {
        // Convert dot notation to nested object access for error checking
        const fieldErrors = fieldPath
          .split(".")
          .reduce((obj, key) => obj?.[key], validationErrors);
        return fieldErrors !== undefined;
      });

      if (stepErrors.length > 0) {
        // Use Promise.all to ensure all fields are marked as touched
        await Promise.all(
          currentStepFields.map(async (fieldPath) => {
            // Mark field as touched and trigger validation
            setFieldTouched(fieldPath, true, false);
            // Also trigger validation by setting the field value again
            const currentValue = fieldPath
              .split(".")
              .reduce((obj, key) => obj?.[key], values);
            setFieldValue(fieldPath, currentValue, true);
          }),
        );

        // Force a re-render by triggering validation again
        const revalidationErrors = await validateForm();
        console.log("Re-validation errors:", revalidationErrors);

        // Add a small delay to ensure state updates are processed
        await new Promise((resolve) => setTimeout(resolve, 100));

        return false;
      }

      return true;
    } catch (error) {
      console.error("Validation error:", error);
      return false;
    }
  };

  const handleNext = async () => {
    const isStepValid = await validateCurrentStep();

    if (isStepValid) {
      try {
        if (!acquirerSelected) {
          if (!enrollmentDataById?.id) {
            dispatch(
              createEnrollmentLoad({
                token,
                formData: values,
                userMail: userInfo?.email || "",
                currentStep: activeStep + 1,
                enrollment_type: "ACQUIRER",
              }),
            );
          } else {
            dispatch(
              updateEnrollmentLoad({
                token,
                formData: values,
                userMail: userInfo?.email || "",
                enrollmentId: {
                  id: enrollmentSelected || enrollmentDataById?.id,
                },
                currentStep: activeStep + 1,
                enrollment_type: "ACQUIRER",
              }),
            );
          }
        }
        dispatch(setActiveStep(activeStep + 1));
      } catch (error) {
        console.error("Error saving enrollment:", error);
      }
    }
  };

  const handleSave = async () => {
    const isStepValid = await validateCurrentStep();
    if (isStepValid) {
      handleSubmit();
    }
  };

  return (
    <AcquirerFormRootArea padding="24px 0">
      <GridItem xs={12}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: "pointer",
                  "& .MuiStepLabel-label": {
                    color:
                      activeStep === index ? "primary.main" : "text.secondary",
                    fontWeight: activeStep === index ? 700 : 400,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </GridItem>
      <GridItem xs={12} padding="10px 0 0">
        {statusRetrieve === "loading" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {statusRetrieve !== "loading" && (
          <>
            <GridContainer>
              {activeStep === steps.indexOf("Acquirer Info") && (
                <AcquirerInfo
                  formValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  currencies={currencies}
                  modeForm={modeForm}
                />
              )}
            </GridContainer>
            <GridContainer>
              {activeStep === steps.indexOf("Acquirer Commissions") && (
                <AcquirerCommissions
                  formValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  errors={errors}
                  touched={touched}
                />
              )}
            </GridContainer>
            <GridContainer>
              {activeStep === steps.indexOf("Bank Info") && (
                <BankInfo
                  formValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              )}
            </GridContainer>
          </>
        )}
      </GridItem>
      <GridContainer item xs={12} margin="20px 0 0" justifyContent="flex-start">
        <GridItem xs={6} md={3} lg={1} margin="0 10px 0 0">
          <Button
            variant="outlined"
            color="primary"
            onClick={goBack}
            sx={{ marginRight: 1 }}
          >
            Cancel
          </Button>
        </GridItem>
        <GridItem xs={6} md={3} lg={1} margin="0 10px 0 0">
          <Button
            variant="outlined"
            color="primary"
            disabled={isFirstStep}
            onClick={handleBack}
          >
            Back
          </Button>
        </GridItem>
        <GridItem xs={6} md={3} lg={1}>
          <Button
            disabled={isSubmitting || status === "loading"}
            variant="contained"
            color="primary"
            onClick={isLastStep ? handleSave : handleNext}
          >
            {!isLastStep ? "Next" : modeForm === "create" ? "Save" : "Update"}
          </Button>
        </GridItem>
      </GridContainer>
    </AcquirerFormRootArea>
  );
};

export default AcquirerForm;
